import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  FolderOpen as FolderIcon,
  Description as FileIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { batchUploadTestData, quickUploadIncidentFiles } from '../utils/testDataUploader';

const TestUploadPage: React.FC = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState('');
  const [uploadResults, setUploadResults] = useState<{
    succeeded: string[];
    failed: { file: string; error: Error }[];
  } | null>(null);
  const [category, setCategory] = useState('incident');
  const categoryLabel = t('testUpload.category');
  const categoryOptions = [
    { value: 'incident', label: t('testUpload.categories.incident') },
    { value: 'finance', label: t('testUpload.categories.finance') },
    { value: 'planning', label: t('testUpload.categories.planning') },
    { value: 'analytics', label: t('testUpload.categories.analytics') },
    { value: 'engineering', label: t('testUpload.categories.engineering') },
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      setSelectedFiles(files);
      setUploadResults(null);
    }
  };

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value);
  };

  const handleStartUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadResults(null);

    try {
      const result = await batchUploadTestData(selectedFiles, {
        category,
        tags: category === 'incident' ? ['過去トラ', 'インシデント'] : [],
        department: 'Operations',
        classification: 'internal',
        onProgress: (progress, fileName) => {
          setUploadProgress(progress);
          setCurrentFile(fileName);
        },
        onError: (error, fileName) => {
          console.error(`Failed to upload ${fileName}:`, error.message);
        },
        onSuccess: (fileName) => {
          console.log(`Successfully uploaded: ${fileName}`);
        },
      });

      setUploadResults(result);
    } catch (error) {
      console.error('Batch upload failed:', error);
    } finally {
      setUploading(false);
      setUploadProgress(100);
    }
  };

  const handleQuickIncidentUpload = () => {
    // インシデントファイル用のファイル選択ダイアログを開く
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf';

    input.addEventListener('change', async (event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const files = Array.from(target.files);
        setSelectedFiles(files);
        setCategory('incident');

        // 自動でアップロード開始
        setUploading(true);
        setUploadProgress(0);
        setUploadResults(null);

        try {
          await quickUploadIncidentFiles(files);

          const result = await batchUploadTestData(files, {
            category: 'incident',
            tags: ['過去トラ', 'インシデント', '対応済み'],
            department: 'Operations',
            classification: 'internal',
            onProgress: (progress, fileName) => {
              setUploadProgress(progress);
              setCurrentFile(fileName);
            },
          });

          setUploadResults(result);
        } catch (error) {
          console.error('Quick upload failed:', error);
        } finally {
          setUploading(false);
        }
      }
    });

    input.click();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('testUpload.title')}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('testUpload.description')}{' '}
        {t('testUpload.incidentDirectory')}
      </Typography>

      {/* クイックアップロードセクション */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('testUpload.quickUpload')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={handleQuickIncidentUpload}
            disabled={uploading}
            sx={{ mr: 2 }}
          >
            {t('testUpload.selectIncidentFiles')}
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            {t('testUpload.quickUploadHelp')}
          </Typography>
        </CardContent>
      </Card>

      {/* 詳細アップロードセクション */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('testUpload.detailedUpload')}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControl sx={{ minWidth: 200, mr: 2 }}>
              <InputLabel id="test-upload-category-label">{categoryLabel}</InputLabel>
              <Select
                labelId="test-upload-category-label"
                value={category}
                onChange={handleCategoryChange}
                label={categoryLabel}
                disabled={uploading}
              >
                {categoryOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              component="label"
              startIcon={<FolderIcon />}
              disabled={uploading}
            >
              {t('testUpload.selectFiles')}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.xlsx"
                hidden
                onChange={handleFileSelect}
              />
            </Button>
          </Box>

          {selectedFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('testUpload.selectedFiles', { count: selectedFiles.length })}
              </Typography>
              <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', p: 1 }}>
                <List dense>
                  {selectedFiles.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <FileIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={<StartIcon />}
            onClick={handleStartUpload}
            disabled={uploading || selectedFiles.length === 0}
          >
            {t('testUpload.startUpload')}
          </Button>
        </CardContent>
      </Card>

      {/* アップロード進捗 */}
      {uploading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('testUpload.uploading')}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {uploadProgress.toFixed(1)}% - {currentFile}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* アップロード結果 */}
      {uploadResults && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('testUpload.results')}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={t('testUpload.successCount', { count: uploadResults.succeeded.length })}
                color="success"
                sx={{ mr: 1 }}
              />
              <Chip
                icon={<ErrorIcon />}
                label={t('testUpload.failureCount', { count: uploadResults.failed.length })}
                color="error"
              />
            </Box>

            {uploadResults.succeeded.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  {t('testUpload.succeededFiles')}
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', p: 1, mb: 2 }}>
                  <List dense>
                    {uploadResults.succeeded.map((fileName, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={fileName} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </>
            )}

            {uploadResults.failed.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  {t('testUpload.failedFiles')}
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', p: 1 }}>
                  <List dense>
                    {uploadResults.failed.map(({ file, error }, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <ErrorIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={file}
                          secondary={error.message}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* 使用方法 */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('testUpload.instructionsTitle')}
        </Typography>
        <Typography variant="body2" component="div">
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>{t('testUpload.instructions.quick')}</li>
            <li>{t('testUpload.instructions.detailed')}</li>
            <li>{t('testUpload.instructions.metadata')}</li>
            <li>{t('testUpload.instructions.review')}</li>
          </ol>
        </Typography>
      </Alert>
    </Box>
  );
};

export default TestUploadPage;
